"use client";

import React, { useEffect, useState } from "react";
import { ShieldCheck, Loader, Check, CircleAlert } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const StyledTextarea = React.forwardRef<HTMLTextAreaElement, React.TextareaHTMLAttributes<HTMLTextAreaElement>>(({
  className,
  ...props
}, ref) => {
  return <textarea className={cn("flex min-h-[120px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50", className)} ref={ref} {...props} />;
});
StyledTextarea.displayName = "StyledTextarea";
const StyledSelect = React.forwardRef<HTMLSelectElement, React.SelectHTMLAttributes<HTMLSelectElement>>(({
  className,
  children,
  ...props
}, ref) => {
  return <div className="relative">
      <select className={cn("flex h-14 w-full items-center justify-between rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs placeholder:text-muted-foreground focus:outline-none focus:ring-[3px] focus:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50 appearance-none pt-6", className)} ref={ref} {...props}>
        {children}
      </select>
      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-muted-foreground">
        <svg className="h-4 w-4 fill-current" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
          <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
        </svg>
      </div>
    </div>;
});
StyledSelect.displayName = "StyledSelect";
export default function SecureInquiryPortal() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="secure-inquiry-portal" className="relative w-full bg-white py-24 px-4 md:px-6 lg:px-8 overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute -top-[20%] -right-[10%] w-[50%] h-[50%] rounded-full bg-teal-50/50 blur-3xl" />
        <div className="absolute top-[40%] -left-[10%] w-[40%] h-[40%] rounded-full bg-blue-50/50 blur-3xl" />
      </div>

      <div ref={ref} className={cn("relative mx-auto max-w-[600px] transition-all duration-1000 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10")}>
        <div className="mb-8 text-center">
          <Badge variant="outline" className="mb-4 border-teal-200 bg-teal-50 text-teal-800 hover:bg-teal-100 px-3 py-1 gap-1.5 transition-colors">
            <ShieldCheck className="h-3.5 w-3.5" />
            HIPAA Compliant
          </Badge>
          <h2 className="text-3xl font-bold tracking-tight text-primary sm:text-4xl mb-3">
            Secure Inquiry Portal
          </h2>
          <p className="text-muted-foreground">
            Your privacy is our priority. Send us a secure message below.
          </p>
        </div>

        <div className="rounded-xl border border-slate-100 bg-white p-6 shadow-xl shadow-slate-200/40 sm:p-8">
          <FormProvider formId="secure_inquiry_portal_contact" sectionName="secure-inquiry-portal" defaultValues={{
          full_name: "",
          email_address: "",
          phone_number: "",
          inquiry_type: "",
          message: "",
          privacy_consent: false
        }} validationRules={{
          full_name: {
            required: "Full Name is required"
          },
          email_address: {
            required: "Email Address is required",
            pattern: {
              value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
              message: "Invalid email address"
            }
          },
          inquiry_type: {
            required: "Please select an inquiry type"
          },
          message: {
            required: "Message is required"
          },
          privacy_consent: {
            required: "You must consent to the privacy policy"
          }
        }} submitText="Send Secure Message" successMessage="Your secure message has been received. We will contact you shortly." errorMessage="There was a problem sending your message. Please try again.">
            <SecureContactForm />
          </FormProvider>
        </div>
      </div>
    </section>;
}
function SecureContactForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center animate-in fade-in zoom-in duration-500">
        <div className="mb-4 rounded-full bg-teal-50 p-4 text-teal-600 ring-1 ring-teal-100">
          <Check className="h-8 w-8" />
        </div>
        <h3 className="mb-2 text-xl font-semibold text-primary">
          Message Sent Securely
        </h3>
        <p className="mb-8 max-w-xs text-muted-foreground">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="border-teal-200 text-teal-700 hover:bg-teal-50 hover:text-teal-800">
          Send Another Message
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      {}
      <FormField control={control} name="full_name" render={({
      field
    }) => <FormItem className="relative space-y-0">
            <FormControl>
              <Input placeholder=" " className="peer h-14 pt-6 pb-2 border-slate-200 bg-slate-50/30 focus-visible:border-teal-500 focus-visible:ring-teal-500/20 transition-all" {...field} />
            </FormControl>
            <FormLabel className="absolute left-3 top-4 z-10 origin-[0] -translate-y-3 scale-75 transform text-muted-foreground duration-200 peer-placeholder-shown:translate-y-0 peer-placeholder-shown:scale-100 peer-focus:-translate-y-3 peer-focus:scale-75 peer-focus:text-teal-600 cursor-text">
              Full Name
            </FormLabel>
            <FormMessage className="mt-1 text-xs" />
          </FormItem>} />

      {}
      <FormField control={control} name="email_address" render={({
      field
    }) => <FormItem className="relative space-y-0">
            <FormControl>
              <Input type="email" placeholder=" " className="peer h-14 pt-6 pb-2 border-slate-200 bg-slate-50/30 focus-visible:border-teal-500 focus-visible:ring-teal-500/20 transition-all" {...field} />
            </FormControl>
            <FormLabel className="absolute left-3 top-4 z-10 origin-[0] -translate-y-3 scale-75 transform text-muted-foreground duration-200 peer-placeholder-shown:translate-y-0 peer-placeholder-shown:scale-100 peer-focus:-translate-y-3 peer-focus:scale-75 peer-focus:text-teal-600 cursor-text">
              Email Address
            </FormLabel>
            <FormMessage className="mt-1 text-xs" />
          </FormItem>} />

      {}
      <FormField control={control} name="phone_number" render={({
      field
    }) => <FormItem className="relative space-y-0">
            <FormControl>
              <Input type="tel" placeholder=" " className="peer h-14 pt-6 pb-2 border-slate-200 bg-slate-50/30 focus-visible:border-teal-500 focus-visible:ring-teal-500/20 transition-all" {...field} />
            </FormControl>
            <FormLabel className="absolute left-3 top-4 z-10 origin-[0] -translate-y-3 scale-75 transform text-muted-foreground duration-200 peer-placeholder-shown:translate-y-0 peer-placeholder-shown:scale-100 peer-focus:-translate-y-3 peer-focus:scale-75 peer-focus:text-teal-600 cursor-text">
              Phone Number <span className="text-xs opacity-70">(Optional)</span>
            </FormLabel>
            <FormMessage className="mt-1 text-xs" />
          </FormItem>} />

      {}
      <FormField control={control} name="inquiry_type" render={({
      field
    }) => <FormItem className="relative space-y-0">
            <FormControl>
              <StyledSelect className="peer border-slate-200 bg-slate-50/30 focus:border-teal-500 focus:ring-teal-500/20 transition-all" {...field}>
                <option value="" disabled hidden></option>
                <option value="General Question">General Question</option>
                <option value="Appointment Request">Appointment Request</option>
                <option value="Medication Inquiry">Medication Inquiry</option>
                <option value="Genetic Evaluation">Genetic Evaluation</option>
                <option value="Other">Other</option>
              </StyledSelect>
            </FormControl>
            <FormLabel className="absolute left-3 top-4 z-10 origin-[0] -translate-y-3 scale-75 transform text-muted-foreground duration-200 peer-focus:-translate-y-3 peer-focus:scale-75 peer-focus:text-teal-600 pointer-events-none">
              Type of Inquiry
            </FormLabel>
            <FormMessage className="mt-1 text-xs" />
          </FormItem>} />

      {}
      <FormField control={control} name="message" render={({
      field
    }) => <FormItem className="relative space-y-0">
            <FormControl>
              <StyledTextarea placeholder=" " className="peer min-h-[140px] pt-6 pb-2 border-slate-200 bg-slate-50/30 focus-visible:border-teal-500 focus-visible:ring-teal-500/20 transition-all resize-y" {...field} />
            </FormControl>
            <FormLabel className="absolute left-3 top-4 z-10 origin-[0] -translate-y-3 scale-75 transform text-muted-foreground duration-200 peer-placeholder-shown:translate-y-0 peer-placeholder-shown:scale-100 peer-focus:-translate-y-3 peer-focus:scale-75 peer-focus:text-teal-600 cursor-text">
              Message
            </FormLabel>
            <p className="text-[10px] text-muted-foreground text-right pt-1">
              The more detail you give, the faster we can respond securely.
            </p>
            <FormMessage className="mt-1 text-xs" />
          </FormItem>} />

      {}
      <FormField control={control} name="privacy_consent" render={({
      field: {
        value,
        onChange,
        ...field
      }
    }) => <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border border-slate-100 bg-slate-50/50 p-4">
            <FormControl>
              <input type="checkbox" checked={value} onChange={onChange} className="mt-1 h-4 w-4 shrink-0 rounded-sm border-slate-300 text-teal-600 focus:ring-teal-500 cursor-pointer accent-teal-600" {...field} />
            </FormControl>
            <div className="space-y-1 leading-none">
              <FormLabel className="text-sm font-normal text-muted-foreground cursor-pointer">
                I consent to the collection and processing of my personal data
                in accordance with the privacy policy.
              </FormLabel>
              <p className="text-xs text-muted-foreground/70">
                Your information will be stored securely and used solely for
                responding to your inquiry.
              </p>
              <FormMessage className="text-xs" />
            </div>
          </FormItem>} />

      {submitError && <div className="flex items-center gap-2 rounded-md bg-red-50 p-3 text-sm text-red-800 border border-red-100">
          <CircleAlert className="h-4 w-4 shrink-0" />
          <p>{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-12 bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white shadow-lg shadow-teal-500/20 transition-all duration-300 text-base font-medium">
        {isSubmitting ? <>
            <Loader className="mr-2 h-4 w-4 animate-spin" />
            Sending Securely...
          </> : "Send Secure Message"}
      </Button>
    </Form>;
}